import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, SidebarShell } from '@flowcore-water/ui';
import type { AppShellUser, NavItem } from '@flowcore-water/ui';
import { AttestationBanner } from './attestation/AttestationBanner';
import { useTrainingTrackerBugReport } from '../services/bugReporting';

interface LayoutProps {
  children: React.ReactNode;
}

function buildNavItems(role?: string): NavItem[] {
  const items: NavItem[] = [
    { to: '/tree', label: 'My Skills', icon: '🛠️' },
    { to: '/library', label: 'Library', icon: '📚' },
  ];
  const manualItem: NavItem = { to: '/water-well-technician-training', label: 'Water Well Technician Training', icon: '💧' };

  if (role === 'instructor' || role === 'admin' || role === 'outside_observer') {
    items.push({ to: '/dashboard', label: 'Dashboard', icon: '📈' });
  }
  // Visible to everyone (apprentices read the weekly manual); sits directly under Dashboard
  items.push(manualItem);

  if (role === 'admin' || role === 'outside_observer') {
    items.push(
      { to: '/admin/users', label: 'Users', icon: '👥' },
      { to: '/admin/skill-trees', label: 'Skill Trees', icon: '👷' },
      { to: '/admin/attestations', label: 'Compliance', icon: '✅' },
      { to: '/admin/monitoring', label: 'Monitoring', icon: '🔍' },
    );
  }

  return items;
}

/** Flowcore logo — swaps between default, retro, and light variants */
function FlowcoreLogo({ theme }: { theme: string }) {
  if (theme === 'retro') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <img src="/retro-favicon2.png" alt="Flowcore" style={{ height: 32, width: 'auto', borderRadius: 4 }} />
        <img src="/flowcore-retro.png" alt="FLOWCORE" style={{ height: 24, width: 'auto', filter: 'drop-shadow(0 0 8px rgba(5, 217, 232, 0.4))' }} />
      </div>
    );
  }
  if (theme === 'light') {
    return <img src="/flowcore-logo-blue.svg" alt="Flowcore" className="sidebar-shell-logo" />;
  }
  return <img src="/flowcore-logo.svg" alt="Flowcore" className="sidebar-shell-logo" />;
}

/** Synthwave background with gradient overlay */
function SynthwaveBackground() {
  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(/synthwave.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(13,2,33,0.2) 0%, rgba(13,2,33,0.5) 40%, rgba(13,2,33,0.8) 70%, rgba(13,2,33,0.95) 100%)' }} />
    </div>
  );
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { t, theme, setTheme, isRetro } = useTheme();
  const navigate = useNavigate();
  const bugReport = useTrainingTrackerBugReport(user);

  // Favicon swap — app-level concern, not the shell's job
  useEffect(() => {
    const link = document.getElementById('favicon') as HTMLLinkElement | null;
    if (link) {
      link.href = isRetro ? '/retro-favicon2.png' : theme === 'light' ? '/flowcore-logo-blue.svg' : '/flowcore-logo.svg';
      link.type = isRetro ? 'image/png' : 'image/svg+xml';
    }
  }, [isRetro, theme]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const shellUser: AppShellUser | undefined = user
    ? {
        displayName: user.displayName ?? 'User',
        role: user.role === 'outside_observer' ? 'observer' : (user.role ?? 'student'),
        onSignOut: handleSignOut,
      }
    : undefined;

  return (
    <SidebarShell
      theme={t}
      appSlug="training-tracker"
      appTitle="Training Tracker"
      navItems={buildNavItems(user?.role)}
      user={shellUser}
      logo={<FlowcoreLogo theme={theme} />}
      themeName={theme}
      onThemeChange={setTheme}
      background={isRetro ? <SynthwaveBackground /> : undefined}
      topBanner={<AttestationBanner />}
      {...({ bugReport } as { bugReport: typeof bugReport })}
    >
      {children}
    </SidebarShell>
  );
};
